import React, { useState } from 'react';
import Icon from './Icon';
import { janela } from '../services/plataforma';
import {
  situacaoDoPrazo, textoDoPrazo, horarioFormatado, quantosVencidos,
  maisUrgente, numeroDoTicket, assuntoDoTicket, clienteDoTicket,
  ANTECEDENCIA_MIN,
} from '../services/filaRetorno';

// ─────────────────────────────────────────────────────────────────────────────
// Fila de retorno — o card da tela principal, o detalhe e o aviso.
//
// São os tickets com hora marcada para voltar ao cliente (status 5 no MySuite).
// Diferente das outras filas, esta tem COMPROMISSO: alguém está esperando num
// horário combinado.
//
// Por isso o card não mostra só a quantidade. Mostra o próximo horário e o
// quanto falta — que é a informação de que o operador precisa para decidir se
// começa outra coisa agora ou não.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A cor de cada situação, no vocabulário de destaques do Orion.
 *
 * O card mora na tela o tempo todo, então só o que exige ação AGORA pinta de
 * alerta: o que venceu e o que está dentro dos 5 minutos de antecedência. Um
 * retorno marcado para amanhã não pode deixar a tela vermelha o dia inteiro —
 * um alerta que está sempre aceso deixa de ser alerta.
 */
const COR_DA_SITUACAO = {
  vencido:    'd-risco',
  iminente:   'd-risco',
  proximo:    'd-info',
  distante:   'd-info',
  'sem-prazo': 'd-info',
};

const ROTULO_DA_SITUACAO = {
  vencido:    'venceu',
  iminente:   'agora',
  proximo:    'em breve',
  distante:   'agendado',
  'sem-prazo': 'sem horário',
};

/** Uma linha da lista de retornos. */
function LinhaRetorno({ ticket, agora, aoAbrir }) {
  const situacao = situacaoDoPrazo(ticket, agora);
  const numero = numeroDoTicket(ticket);
  const cliente = clienteDoTicket(ticket);
  const urgente = situacao === 'vencido' || situacao === 'iminente';

  return (
    <div
      className="sol-card"
      style={{ '--tcol': urgente ? 'var(--warn)' : 'var(--c-aguard)' }}
      onClick={() => numero != null && aoAbrir(numero, ticket)}
    >
      <div className="sol-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="sol-meta">
            <span className={`selo-dest ${COR_DA_SITUACAO[situacao] || 'd-info'}`}>
              {ROTULO_DA_SITUACAO[situacao]}
            </span>
            <span className="fr-prazo">{textoDoPrazo(ticket, agora)}</span>
            {horarioFormatado(ticket) && (
              <span className="sol-modulo">/ {horarioFormatado(ticket)}</span>
            )}
          </div>
          <div className="sol-id">
            {numero != null ? `#${numero}` : 'sem número'}
            {cliente ? ` · ${cliente}` : ''}
          </div>
          <div className="sol-desc">{assuntoDoTicket(ticket)}</div>
        </div>
        {numero != null && (
          <div className="sol-actions">
            <button
              className="btn btn-ghost btn-sm"
              onClick={(e) => { e.stopPropagation(); aoAbrir(numero, ticket); }}
            >
              Abrir
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * O aviso da tela principal — o bloco que chama para o retorno de agora.
 *
 * Ele NÃO é o lugar de saber quantos retornos existem: isso é o cartão "Sua
 * fila de retorno", na linha de cartões, que está sempre lá com a contagem e
 * abre a lista completa. Os dois na tela ao mesmo tempo, dizendo o mesmo
 * número, é repetição.
 *
 * Por isso este bloco só aparece quando há o que fazer AGORA: um retorno
 * dentro dos 5 minutos de antecedência (ANTECEDENCIA_MIN) ou já vencido. Aí
 * ele mostra qual é o ticket e quanto falta — o que um número sozinho não diz.
 */
export function CardFilaRetorno({ tickets, excedidos = [], agora, carregando, aoAbrirTicket }) {
  const [aberto, setAberto] = useState(false);

  const lista = tickets || [];
  const proximo = lista.length ? maisUrgente(lista, agora) : null;
  const situacao = proximo ? situacaoDoPrazo(proximo, agora) : 'sem-prazo';

  // Nada na hora: quem conta a fila é o cartão lá de cima. Os excedidos seguram
  // o bloco de pé sozinhos — o alerta deles já saiu e foi embora, e este virou
  // o único lugar onde eles ainda aparecem.
  const temExcedido = excedidos.length > 0;
  if (situacao !== 'iminente' && situacao !== 'vencido' && !temExcedido) return null;

  // Os vencidos que AINDA NÃO viraram "agendamento excedido" — ou seja, os que
  // ainda vão receber o seu alerta. Os outros já estão listados um a um logo
  // abaixo, e contá-los de novo aqui seria dizer o mesmo número duas vezes no
  // mesmo cartão.
  const vencidos = quantosVencidos(lista, agora) - excedidos.length;
  const cor = COR_DA_SITUACAO[situacao] || 'd-info';

  const abrirTicket = (numero, ticket) => {
    setAberto(false);
    aoAbrirTicket(numero, ticket);
  };

  return (
    <>
      <div
        className={`panel panel-dest ${cor} fr-card`}
        onClick={() => setAberto(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setAberto(true); }}
      >
        <div className="fr-card-head">
          <div className="fr-card-titulo">
            <Icon name="clock" size={15} />
            <span>Fila de retorno</span>
            <span className="fr-contagem">{lista.length}</span>
          </div>
          {carregando && <span className="fr-carregando">atualizando…</span>}
        </div>

        {proximo && (
          <div className="fr-card-corpo">
            <span className="fr-proximo">
              Próximo: <b>#{numeroDoTicket(proximo)}</b> {textoDoPrazo(proximo, agora)}
            </span>
            {horarioFormatado(proximo) && (
              <span className="fr-hora">{horarioFormatado(proximo)}</span>
            )}
          </div>
        )}

        {vencidos > 0 && (
          <div className="fr-vencidos">
            {vencidos === 1 ? '1 retorno venceu' : `${vencidos} retornos venceram`}
          </div>
        )}

        {/* Agendamento excedido — o destino de quem já teve o seu alerta.
            O modal aparece uma vez e sai; daqui o ticket só sai quando for
            reagendado (outra data de previsão) ou deixar a fila de retorno.
            Fica listado ticket a ticket, e não como contagem, porque a essa
            altura o operador precisa saber QUAL, não QUANTOS. */}
        {temExcedido && (
          <div className="fr-excedidos">
            <div className="fr-excedidos-titulo">
              <Icon name="alert-circle" size={13} />
              Agendamento excedido
              <span className="fr-contagem">{excedidos.length}</span>
            </div>
            {excedidos.map((t, i) => {
              const num = numeroDoTicket(t);
              return (
                <button
                  key={num ?? `excedido-${i}`}
                  type="button"
                  className="fr-excedido-item"
                  onClick={(e) => { e.stopPropagation(); if (num != null) abrirTicket(num, t); }}
                >
                  <b>#{num ?? '—'}</b>
                  <span className="fr-excedido-prazo">{textoDoPrazo(t, agora)}</span>
                  <span className="fr-excedido-assunto">{assuntoDoTicket(t)}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="campo-dica fr-dica">Clique para ver os prazos</div>
      </div>

      {aberto && (
        <DetalheFilaRetorno
          tickets={tickets}
          agora={agora}
          aoFechar={() => setAberto(false)}
          aoAbrirTicket={abrirTicket}
        />
      )}
    </>
  );
}

/** A lista completa, com o prazo de cada um. */
export function DetalheFilaRetorno({ tickets, agora, aoFechar, aoAbrirTicket }) {
  return (
    <div className="drawer-overlay" onClick={aoFechar}>
      <div className="modal-nx modal-largo" onClick={e => e.stopPropagation()}>
        <div className="drawer-head">
          <div className="drawer-title">
            Fila de retorno
            <span className="drawer-sub">
              {tickets.length === 1 ? '1 ticket agendado' : `${tickets.length} tickets agendados`}
            </span>
          </div>
        </div>

        <div className="drawer-body">
          <div className="campo-dica" style={{ marginBottom: 12 }}>
            Tickets com retorno combinado com o cliente. O alerta aparece{' '}
            {ANTECEDENCIA_MIN} minutos antes do horário e sai <b>uma única vez</b>{' '}
            por agendamento. Depois disso o ticket fica em “Agendamento excedido”,
            no cartão da tela de início, até ser reagendado ou sair da fila de
            retorno — reagendar dá direito a um alerta novo.
          </div>

          {tickets.map((t, i) => (
            <LinhaRetorno
              key={numeroDoTicket(t) ?? `sem-numero-${i}`}
              ticket={t}
              agora={agora}
              aoAbrir={aoAbrirTicket}
            />
          ))}
        </div>

        <div className="drawer-foot">
          <button className="btn" onClick={aoFechar}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

/**
 * O aviso da hora marcada.
 *
 * Segue o mesmo desenho do alerta de ociosidade — modal na frente e um bip —
 * porque o motivo é o mesmo: um toast que some em cinco segundos não cumpre
 * função se o operador está de costas para a tela. A diferença é que aqui o bip
 * toca UMA vez: existe uma hora marcada e o reaviso já está no hook, então
 * apitar sem parar seria punição, não lembrete.
 */
export function AlertaRetorno({ ticket, demais = [], agora, aoAtender, aoDispensar }) {
  const numero = numeroDoTicket(ticket);
  const vencido = situacaoDoPrazo(ticket, agora) === 'vencido';

  // Atender assume o ticket no MySuite antes de abrir a formalização, e isso
  // leva um instante. Sem este estado o botão ficaria mudo no meio do caminho —
  // e um segundo clique mandaria o mesmo ticket duas vezes.
  const [assumindo, setAssumindo] = useState(false);

  React.useEffect(() => {
    try { janela.restaurar(); } catch (e) { /* no navegador não há o que restaurar */ }

    const Contexto = window.AudioContext || window.webkitAudioContext;
    if (!Contexto) return undefined;

    let ctx;
    try { ctx = new Contexto(); } catch (e) { return undefined; }

    try {
      const osc = ctx.createOscillator();
      const ganho = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      ganho.gain.value = 0.06;
      osc.connect(ganho).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.24);
    } catch (e) { /* o navegador pode bloquear áudio sem interação prévia */ }

    return () => { try { ctx.close(); } catch (e) { /* já fechado */ } };
  }, []);

  return (
    <div className="drawer-overlay drawer-overlay-alto">
      <div className="modal-nx modal-estreito" onClick={e => e.stopPropagation()}>
        <div className="drawer-head">
          <div className="drawer-title">
            {vencido ? 'Retorno atrasado' : 'Retorno se aproximando'}
            <span className="drawer-sub">{textoDoPrazo(ticket, agora)}</span>
          </div>
        </div>

        <div className="drawer-body">
          <div className={`panel panel-dest ${vencido ? 'd-risco' : 'd-atual'}`} style={{ marginBottom: 0 }}>
            <div style={{ marginBottom: 8 }}>
              <Icon name="clock" size={16} style={{ marginRight: 8 }} />
              {vencido
                ? 'O horário combinado com o cliente já passou.'
                : `O horário combinado com o cliente está chegando.`}
            </div>

            <div className="fld-label">Ticket</div>
            <div className="fld-value">
              {numero != null ? `#${numero}` : 'sem número'}
              {horarioFormatado(ticket) ? ` · marcado para ${horarioFormatado(ticket)}` : ''}
            </div>

            <div className="fld-label" style={{ marginTop: 8 }}>Assunto</div>
            <div className="fld-value">{assuntoDoTicket(ticket)}</div>

            {clienteDoTicket(ticket) && (
              <>
                <div className="fld-label" style={{ marginTop: 8 }}>Cliente</div>
                <div className="fld-value">{clienteDoTicket(ticket)}</div>
              </>
            )}

            {demais.length > 0 && (
              <div className="campo-dica" style={{ marginTop: 10 }}>
                {demais.length === 1
                  ? 'Há mais 1 retorno no mesmo intervalo.'
                  : `Há mais ${demais.length} retornos no mesmo intervalo.`}{' '}
                Veja todos no card da fila de retorno.
              </div>
            )}
          </div>
        </div>

        <div className="drawer-foot">
          <button className="btn" onClick={aoDispensar}>Depois</button>
          {numero != null && (
            <button
              className="btn btn-primary"
              onClick={async () => {
                setAssumindo(true);
                // Sem `finally`: se der certo, este alerta sai da tela junto
                // com o atendimento que abre. Se der errado, o botão volta a
                // funcionar — o aviso do contexto já disse o porquê.
                try { await aoAtender(numero); } catch (e) { /* tratado no contexto */ }
                setAssumindo(false);
              }}
              disabled={assumindo}
              autoFocus
            >
              {assumindo
                ? <><Icon name="rotate-cw" size={14} className="orion-spin" /> Assumindo…</>
                : <><Icon name="ticket" size={14} /> Atender o {numero}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
